import {
  DomainAddressType,
  DomainIncomeType,
  type DtoCreateEmploymentReq,
  type DtoGetBorrowersEmploymentsResp,
  type DtoUpdateEmploymentReq,
  type DomainFrequencyType,
} from "@porchpass/api-client";
import { createIsoDate, parseIsoDate, safeNumber } from "src/utils";

export type EmploymentFormData = {
  id?: string;
  employer_name?: string;
  position_title?: string;
  is_self_employed?: boolean;
  is_current?: boolean;
  street1?: string;
  city?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  frequency?: DomainFrequencyType;
  amount?: string;
  weekly_hours?: string;
  bonus_frequency?: DomainFrequencyType;
  bonus_amount?: string;
  bonus_weekly_hours?: string;
  commission_frequency?: DomainFrequencyType;
  commission_amount?: string;
  commission_weekly_hours?: string;
  overtime_frequency?: DomainFrequencyType;
  overtime_amount?: string;
  overtime_weekly_hours?: string;
  from_month?: string;
  from_year?: string;
  to_month?: string;
  to_year?: string;
  total_income?: string;
};

export type EmploymentListFormData = {
  employments: EmploymentFormData[];
  hasSufficientEmploymentHistory?: boolean;
};

function buildEmploymentIncomes(data: EmploymentFormData) {
  return [
    {
      amount: safeNumber(data?.amount),
      frequency: data?.frequency,
      income_type: DomainIncomeType.IncomeBase,
      weekly_hours:
        data?.frequency == "HOURLY"
          ? parseInt(data?.weekly_hours ?? "0", 10)
          : undefined,
    },
    {
      amount: safeNumber(data?.bonus_amount),
      frequency: data?.bonus_frequency,
      income_type: DomainIncomeType.IncomeBonus,
      weekly_hours:
        data?.bonus_frequency == "HOURLY"
          ? parseInt(data?.bonus_weekly_hours ?? "0", 10)
          : undefined,
    },
    {
      amount: safeNumber(data?.overtime_amount),
      frequency: data?.overtime_frequency,
      income_type: DomainIncomeType.IncomeOvertime,
      weekly_hours:
        data?.overtime_frequency == "HOURLY"
          ? parseInt(data?.overtime_weekly_hours ?? "0", 10)
          : undefined,
    },
    {
      amount: safeNumber(data?.commission_amount),
      frequency: data?.commission_frequency,
      income_type: DomainIncomeType.IncomeCommission,
      weekly_hours:
        data?.commission_frequency == "HOURLY"
          ? parseInt(data?.commission_weekly_hours ?? "0", 10)
          : undefined,
    },
  ].filter((income) => income.amount > 0 && income.frequency);
}

function buildEmploymentBase(
  data: EmploymentFormData,
  includeAddressType: boolean,
): DtoCreateEmploymentReq {
  const mappedData: DtoCreateEmploymentReq = {
    address: {
      ...(includeAddressType
        ? { address_type: DomainAddressType.AddressEmployer }
        : {}),
      city: data.city ?? "",
      state: data.state ?? "",
      county: data.county ?? "",
      street1: data.street1 ?? "",
      zip_code: data.zip_code ?? "",
    },
    employer_name: data.employer_name ?? "",
    incomes: buildEmploymentIncomes(data),
    is_current: Boolean(data.is_current),
    is_self_employed: Boolean(data.is_self_employed),
    position_title: data.position_title ?? "",
    start_date: createIsoDate(
      parseInt(data.from_year ?? "0", 10),
      parseInt(data.from_month ?? "0", 10),
    ),
  };

  if (!data.is_current) {
    mappedData.end_date = createIsoDate(
      parseInt(data.to_year ?? "0", 10),
      parseInt(data.to_month ?? "0", 10),
    );
  }

  return mappedData;
}

export function toCreateEmploymentReq(
  data: EmploymentFormData,
): DtoCreateEmploymentReq {
  return buildEmploymentBase(data, true);
}

export function toUpdateEmploymentReq(
  data: EmploymentFormData,
): DtoUpdateEmploymentReq {
  return buildEmploymentBase(data, false);
}

export function fromEmploymentsList(
  data: DtoGetBorrowersEmploymentsResp,
): EmploymentListFormData {
  const mappedEmployments = (data.employment_details ?? []).map(
    (employment_detail) => {
      const { address, employment, incomes } = employment_detail ?? {};
      const { month: from_month, year: from_year } = parseIsoDate(
        employment?.start_date ?? "",
      );

      const { month: to_month, year: to_year } = parseIsoDate(
        employment?.end_date ?? "",
      );

      const baseIncome = incomes?.find(
        (x) => x.income_type == DomainIncomeType.IncomeBase,
      );
      const bonusIncome = incomes?.find(
        (x) => x.income_type == DomainIncomeType.IncomeBonus,
      );
      const overtimeIncome = incomes?.find(
        (x) => x.income_type == DomainIncomeType.IncomeOvertime,
      );
      const commissionIncome = incomes?.find(
        (x) => x.income_type == DomainIncomeType.IncomeCommission,
      );

      return {
        id: employment_detail.employment?.id,
        street1: address?.street1 ?? "",
        city: address?.city ?? "",
        state: address?.state ?? "",
        county: address?.county ?? "",
        zip_code: address?.zip_code ?? "",
        start_date: employment?.start_date,
        from_month: from_month ?? "",
        from_year: from_year ?? "",
        end_date: employment?.end_date,
        to_month: to_month ?? "",
        to_year: to_year ?? "",
        employer_name: employment?.employer_name ?? "",
        is_self_employed: Boolean(employment?.is_self_employed),
        is_current: !!employment?.is_current,
        position_title: employment?.position_title ?? "",
        frequency: baseIncome?.frequency,
        amount: String(baseIncome?.amount ?? ""),
        weekly_hours: String(baseIncome?.weekly_hours ?? ""),
        bonus_frequency: bonusIncome?.frequency,
        bonus_amount: String(bonusIncome?.amount ?? ""),
        bonus_weekly_hours: String(bonusIncome?.weekly_hours ?? ""),
        commission_frequency: commissionIncome?.frequency,
        commission_amount: String(commissionIncome?.amount ?? ""),
        commission_weekly_hours: String(commissionIncome?.weekly_hours ?? ""),
        overtime_frequency: overtimeIncome?.frequency,
        overtime_amount: String(overtimeIncome?.amount ?? ""),
        overtime_weekly_hours: String(overtimeIncome?.weekly_hours ?? ""),
        total_income: String(employment_detail.total_income ?? ""),
      };
    },
  );

  return {
    employments: mappedEmployments,
    hasSufficientEmploymentHistory: data.has_sufficient_employment_history,
  };
}
